import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  AutocompleteInput,
  BooleanInput,
  PasswordInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  required,
  useGetList,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { formatUTCDate } from '../../util/formatDate';

const CustomToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

const VisibleArmiesInput = () => {
  const { setValue } = useFormContext();

  const role = useWatch({ name: 'role' });
  const visibleArmies = useWatch({ name: 'visible_armies' }) || [];

  const [selectedArmy, setSelectedArmy] = React.useState(null);
  const [selectedPermission, setSelectedPermission] = React.useState('view');
  const [sourceUser, setSourceUser] = React.useState(null);

  const { data: armies = [], isLoading: armiesLoading } = useGetList('armies', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });

  const { data: users = [], isLoading: usersLoading } = useGetList('users', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'username', order: 'ASC' },
  });

  const armyById = React.useMemo(() => {
    return Object.fromEntries(armies.map((army) => [army.id, army]));
  }, [armies]);

  const normalizedVisibleArmies = React.useMemo(() => {
    return visibleArmies.map((item) => ({
      army: typeof item.army === 'object' ? item.army.id : item.army,
      permission: item.permission || 'view',
    }));
  }, [visibleArmies]);

  const availableArmies = armies.filter(
    (army) => !normalizedVisibleArmies.some((item) => item.army === army.id)
  );

  const sourceUserVisibleArmies = React.useMemo(() => {
    if (!sourceUser?.visible_armies) return [];

    return sourceUser.visible_armies.map((item) => ({
      army: typeof item.army === 'object' ? item.army.id : item.army,
      permission: item.permission || 'view',
    }));
  }, [sourceUser]);

  const sourceUserArmiesToAdd = sourceUserVisibleArmies.filter(
    (sourceItem) =>
      !normalizedVisibleArmies.some((item) => item.army === sourceItem.army)
  );

  if (role === 'admin') {
    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Visible Armies (all)
        </Typography>

        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Admin users can view and edit all armies. No army-specific permissions are needed.
        </Typography>
      </Box>
    );
  }

  const handleAddArmy = () => {
    if (!selectedArmy) return;

    setValue(
      'visible_armies',
      [
        ...normalizedVisibleArmies,
        {
          army: selectedArmy,
          permission: selectedPermission,
        },
      ],
      { shouldDirty: true, shouldValidate: true }
    );

    setSelectedArmy(null);
    setSelectedPermission('view');
  };

  const handleBulkAddFromUser = () => {
    if (!sourceUser || sourceUserArmiesToAdd.length === 0) return;

    setValue(
      'visible_armies',
      [...normalizedVisibleArmies, ...sourceUserArmiesToAdd],
      { shouldDirty: true, shouldValidate: true }
    );

    setSourceUser(null);
  };

  const handleRemoveArmy = (armyId) => {
    setValue(
      'visible_armies',
      normalizedVisibleArmies.filter((item) => item.army !== armyId),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const handlePermissionChange = (armyId, permission) => {
    setValue(
      'visible_armies',
      normalizedVisibleArmies.map((item) =>
        item.army === armyId ? { ...item, permission } : item
      ),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Visible Armies ({normalizedVisibleArmies.length})
      </Typography>

      {normalizedVisibleArmies.length === 0 && (
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
          No armies added.
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        {normalizedVisibleArmies.map((item) => {
          const army = armyById[item.army];

          return (
            <Box
              key={item.army}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid #ddd',
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography>{army ? army.name : item.army}</Typography>
              </Box>

              <Select
                size="small"
                value={item.permission}
                onChange={(event) =>
                  handlePermissionChange(item.army, event.target.value)
                }
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="view">View only</MenuItem>
                <MenuItem value="edit">Can edit</MenuItem>
              </Select>

              <IconButton
                color="error"
                onClick={() => handleRemoveArmy(item.army)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          border: '1px solid #ddd',
          borderRadius: 1,
          p: 2,
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Add one army
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <AutocompleteInput
            label="Add army"
            source="__army_picker"
            choices={availableArmies}
            optionText="name"
            optionValue="id"
            value={selectedArmy}
            onChange={setSelectedArmy}
            isLoading={armiesLoading}
            helperText={false}
            sx={{ minWidth: 300 }}
          />

          <Select
            size="small"
            value={selectedPermission}
            onChange={(event) => setSelectedPermission(event.target.value)}
            sx={{ minWidth: 140, mt: 1 }}
          >
            <MenuItem value="view">View only</MenuItem>
            <MenuItem value="edit">Can edit</MenuItem>
          </Select>

          <Button
            variant="contained"
            onClick={handleAddArmy}
            disabled={!selectedArmy}
            sx={{ mt: 1 }}
          >
            Add
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          border: '1px solid #ddd',
          borderRadius: 1,
          p: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Bulk add from another user
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Autocomplete
            options={users.filter((user) => user.role !== 'admin')}
            getOptionLabel={(user) => user.username || ''}
            value={sourceUser}
            loading={usersLoading}
            onChange={(event, value) => setSourceUser(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{ minWidth: 300 }}
            renderInput={(params) => (
              <TextField {...params} label="Copy armies from user" />
            )}
          />

          <Button
            variant="contained"
            onClick={handleBulkAddFromUser}
            disabled={!sourceUser || sourceUserArmiesToAdd.length === 0}
            sx={{ mt: 1 }}
          >
            Add {sourceUserArmiesToAdd.length || ''}
          </Button>
        </Box>

        {sourceUser && (
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.75 }}>
            {sourceUserArmiesToAdd.length} armies will be added. Existing armies are skipped.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const UserForm = ({ isEdit }) => {
  return (
    <SimpleForm toolbar={<CustomToolbar />}>
      {isEdit && <TextInput source="id" disabled />}

      <TextInput source="username" validate={required()} />

      <SelectInput
        source="role"
        choices={[
          { id: 'user', name: 'user' },
          { id: 'admin', name: 'admin' },
        ]}
        validate={required()}
      />

      {isEdit && <BooleanInput source="active" />}

      {isEdit && (
        <TextInput
          source="created_date"
          label="Created date (UTC)"
          format={(value) => formatUTCDate(value)}
          disabled
        />
      )}

      <PasswordInput
        label={isEdit ? 'Change Password' : 'Password'}
        source="password"
      />

      <VisibleArmiesInput />
    </SimpleForm>
  );
};

export default UserForm;